import type { FirebaseClient, FirebaseClientConfig } from '@akpl/firebase/client';
import type { PublicConfig } from '@akpl/shared';

import { analytics } from './analytics';
import { APP_VERSION, fetchPublicConfig, setTokenProvider } from './api';
import { loadFirebase, prefetchFirebase } from './firebase';
import { initAppI18n } from './i18n';

export interface BootResult {
  config: PublicConfig;
  /**
   * null when `/config` carries no Firebase project ("offline auth"): the shell still renders,
   * but nothing that needs an account works. This is the local-dev default.
   */
  firebase: FirebaseClient | null;
}

export interface BootDeps {
  fetchConfig?: () => Promise<PublicConfig>;
  loadFirebase?: (config: FirebaseClientConfig) => Promise<FirebaseClient | null>;
  initI18n?: () => Promise<unknown>;
}

/**
 * Boot sequence (docs/05 §1), mirroring the TV's:
 * 1. `GET /config` and i18n in parallel (i18n resources are inline, so it never blocks on a
 *    network round-trip)
 * 2. Mixpanel with the token from `/config`
 * 3. Firebase (lazy chunk) + the ID-token provider for the API client
 *
 * It deliberately does **not** wait for `/me`: routes fetch it with TanStack Query so a slow
 * API shows the shell instead of a blank screen.
 */
export async function runBoot({
  fetchConfig = () => fetchPublicConfig(),
  loadFirebase: load = loadFirebase,
  initI18n = () => initAppI18n(),
}: BootDeps = {}): Promise<BootResult> {
  // The Firebase chunk does not depend on `/config`, only its *initialization* does, so it is
  // downloaded next to the `/config` request rather than after it (MOB-56).
  prefetchFirebase();
  const [config] = await Promise.all([fetchConfig(), initI18n()]);
  analytics.init(config.mixpanelToken, {
    env: config.env,
    app_version: APP_VERSION,
    via: 'mobile',
  });

  const firebase = await load(config.firebase).catch((err: unknown) => {
    console.warn('[akapella] firebase init failed', err);
    return null;
  });

  if (firebase) {
    setTokenProvider(() => firebase.getIdToken());
    // Let Firebase restore the persisted user before the first render, so a returning singer
    // never sees the signed-out state flash by.
    await firebase.waitForAuth();
  }

  return { config, firebase };
}
